// ============================================================
// TMR Portal Admin — os papéis de um usuário: ver, conceder e revogar (10 B5, 06 §5)
//
// This is what was missing: roles could be *defined* (Papéis) and granted *at approval time* (Acessos),
// but never given to somebody who already existed — so a person created from this screen had no way to
// receive access at all.
// ============================================================
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Avatar, Btn, StatusBadge } from '../../components/primitives';
import { Skeleton, ErrorState } from '../../components/states';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Icons, StrokeIcon } from '../../lib/icons';
import { useAuthorizationRoles } from '../../data/authorization/queries';
import { useAssignRole, useRevokeRole, useUser } from '../../data/users/queries';
import type { UserListItem, UserRoleItem } from '../../data/users/contract';
import { GrantsEditor, grantDraftError, toGrant } from '../authorization/GrantsEditor';
import type { GrantDraft } from '../authorization/GrantsEditor';
import { ErrorBanner } from '../authorization/ErrorBanner';
import { OverlayPortal } from '../../components/OverlayPortal';

export function UserRolesModal({ user, onClose }: { user: UserListItem; onClose: () => void }) {
  const detail = useUser(user.id);
  const roles = useAuthorizationRoles();
  const assign = useAssignRole(user.id);
  const revoke = useRevokeRole(user.id);

  const [grants, setGrants] = useState<GrantDraft[]>([]);
  const [touched, setTouched] = useState(false);
  const [toRevoke, setToRevoke] = useState<UserRoleItem | null>(null);

  const submit = () => {
    if (grants.length === 0) return;
    if (grants.some(grantDraftError)) {
      setTouched(true);
      return;
    }

    // One call per grant — the endpoint takes a single assignment, and each is validated on its own
    // against the operator's permissions (06 §5.1). Sequential so a refusal stops at the offending one
    // instead of half-applying a batch nobody asked to be atomic.
    void (async () => {
      for (const grant of grants) {
        await assign.mutateAsync(toGrant(grant));
      }
      setGrants([]);
      setTouched(false);
    })();
  };

  const stop = (e: MouseEvent) => e.stopPropagation();
  const current = detail.data?.roles ?? [];

  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={stop} style={{ width: 680, maxWidth: '100%', maxHeight: '90vh', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar name={user.name} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="pa-eyebrow" style={{ marginBottom: 2 }}>Papéis e acesso</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 17, margin: 0, letterSpacing: '-0.01em' }}>{user.name}</h3>
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: 'var(--fg-muted)' }}>{user.email}</p>
            </div>
          </div>

          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16, overflowY: 'auto' }}>
            <ErrorBanner error={assign.error ?? revoke.error} />

            {detail.isPending ? (
              <Skeleton rows={3} />
            ) : detail.isError ? (
              <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
            ) : (
              <>
                <div className="pa-eyebrow">Papéis atuais</div>
                {current.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
                    Esta pessoa não tem nenhum papel — ela entra no portal e não vê nada.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {current.map((role) => (
                      <div key={role.assignmentId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: role.active ? 'var(--cream-50)' : 'var(--cream-100)' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 500, opacity: role.active ? 1 : 0.6 }}>{role.roleName}</div>
                          <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{describeScope(role)}</div>
                        </div>
                        <StatusBadge status={role.active ? 'ativo' : 'inativo'} />
                        <button type="button" onClick={() => setToRevoke(role)} className="pa-icon-btn" style={{ width: 30, height: 30 }} title="Revogar papel">
                          <StrokeIcon d={Icons.x} size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pa-eyebrow" style={{ marginTop: 6 }}>Conceder papel</div>
                <GrantsEditor
                  grants={grants}
                  onChange={setGrants}
                  roles={roles.data ?? []}
                  showErrors={touched}
                  emptyHint="Adicione um papel para conceder acesso a esta pessoa."
                />
              </>
            )}
          </div>

          <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-default)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
            <Btn variant="primary" icon={Icons.check} onClick={submit} disabled={grants.length === 0 || assign.isPending}>
              {assign.isPending ? 'Concedendo…' : 'Conceder'}
            </Btn>
          </div>
        </div>

        {toRevoke && (
          <ConfirmDialog
            title="Revogar papel"
            message={
              <>
                Revogar <strong>{toRevoke.roleName}</strong> de <strong>{user.name}</strong>? A pessoa perde
                agora o acesso que este papel dava.
              </>
            }
            confirmLabel="Revogar"
            icon={Icons.x}
            onConfirm={() => revoke.mutate(toRevoke.assignmentId)}
            onClose={() => setToRevoke(null)}
          />
        )}
      </div>
    </OverlayPortal>
  );
}

/**
 * How far a grant reaches, in words. The ids are not resolved to names here: the detail endpoint returns
 * scope as ids only, and fetching an Empresa per row to render a label would be a request per grant for
 * something the operator can open directly.
 */
function describeScope(role: UserRoleItem): string {
  const reach = role.companyId
    ? 'Escopo: uma Empresa'
    : role.holdingId
      ? 'Escopo: uma Holding'
      : 'Todas as Empresas';

  if (!role.expiresAt) return `${reach} · sem validade`;

  const day = new Date(role.expiresAt).toLocaleDateString('pt-BR');
  return `${reach} · ${role.active ? `até ${day}` : `expirou em ${day}`}`;
}
